import { GitBranch, Plus, Shield, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Environment } from '@/hooks/useProjects';

interface EnvironmentTabsProps {
  environments: Environment[];
  selectedEnvironmentId?: string;
  onSelectEnvironment: (envId: string) => void;
  onCreateEnvironment?: () => void;
  onManageAccess?: (environmentName: string) => void;
  isLoading: boolean;
  /** Whether the environment limit has been reached */
  isAtLimit?: boolean;
  /** Whether the current user can manage permissions */
  canManagePermissions?: boolean;
}

function getParentEnvName(envId: string | undefined, environments: Environment[]): string | null {
  if (!envId) return null;
  const parent = environments.find(e => e.id === envId);
  return parent?.name || null;
}

export function EnvironmentTabs({
  environments,
  selectedEnvironmentId,
  onSelectEnvironment,
  onCreateEnvironment,
  onManageAccess,
  isLoading,
  isAtLimit = false,
  canManagePermissions = false,
}: EnvironmentTabsProps) {
  if (isLoading) {
    return (
      <div className="flex gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-9 w-24 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  const selectedEnv = environments.find((e) => e.id === selectedEnvironmentId);

  return (
    <div className="flex items-center gap-2">
      <ScrollArea className="flex-1 whitespace-nowrap">
        <div className="flex w-max space-x-2 p-1">
          {environments.map((env) => {
            const parentName = getParentEnvName(env.inherits_from, environments);
            const isSelected = selectedEnvironmentId === env.id;

            return (
              <Button
                key={env.id}
                variant={isSelected ? "default" : "ghost"}
                size="sm"
                onClick={() => onSelectEnvironment(env.id)}
                className={cn(
                  "font-medium transition-all relative group h-auto py-1.5",
                  isSelected
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                )}
              >
                <div className="flex flex-col items-start">
                  <span className="flex items-center gap-1.5">
                    {env.name}
                    {parentName && (
                      <GitBranch className={cn(
                        "h-3 w-3",
                        isSelected ? "text-primary-foreground/80" : "text-success"
                      )} />
                    )}
                  </span>
                  {parentName && (
                    <span className={cn(
                      "text-[10px] leading-tight",
                      isSelected ? "text-primary-foreground/70" : "text-muted-foreground/60"
                    )}>
                      from {parentName}
                    </span>
                  )}
                </div>
              </Button>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* Environment options menu */}
      {canManagePermissions && onManageAccess && selectedEnv && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 flex-shrink-0 text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onManageAccess(selectedEnv.name)}>
              <Shield className="h-4 w-4 mr-2" />
              Manage Access
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {onCreateEnvironment && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onCreateEnvironment}
          disabled={isAtLimit}
          className={cn(
            "gap-1 flex-shrink-0",
            isAtLimit
              ? "text-muted-foreground/50 cursor-not-allowed"
              : "text-muted-foreground hover:text-foreground hover:bg-accent"
          )}
          title={isAtLimit ? "Environment limit reached" : "Add environment"}
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="text-xs">Add</span>
        </Button>
      )}
    </div>
  );
}
